/**
 * WhatsAppInfoCard - Muestra información extraída de conversaciones de WhatsApp
 * 
 * Este componente se utiliza en la vista de detalle de evento para mostrar
 * toda la información relevante que DJ Master ha extraído de chats de WhatsApp.
 */

import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';
import { toDisplayText } from '../utils/safeRenderText';

const WhatsAppInfoCard = ({ event, onAddMore }) => {
  if (!event?.has_whatsapp_analysis) {
    return null;
  }

  return (
    <div className="mb-3 p-3 bg-green-900/20 border border-green-700/50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <p className="text-green-400 text-xs font-semibold flex items-center gap-1">
          <MessageCircle className="w-3 h-3" />
          Info extraída de WhatsApp
        </p>
        {onAddMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddMore}
            className="text-xs text-green-400 hover:text-green-300 h-6 px-2"
          >
            + Añadir más
          </Button>
        )}
      </div>
      
      <div className="space-y-1 text-sm">
        {/* Resumen */}
        {event.resumen_whatsapp && (
          <p className="text-slate-300 text-xs italic mb-2">{event.resumen_whatsapp}</p>
        )}
        
        {/* Canciones */}
        {event.cancion_primer_baile && (
          <p className="text-slate-300">💃 <strong>Primer baile:</strong> {event.cancion_primer_baile}</p>
        )}
        {event.cancion_entrada && (
          <p className="text-slate-300">🚪 <strong>Entrada:</strong> {event.cancion_entrada}</p>
        )}
        {event.cancion_entrada_novia && (
          <p className="text-slate-300">👰 <strong>Entrada novia:</strong> {event.cancion_entrada_novia}</p>
        )}
        {event.cancion_entrada_novio && (
          <p className="text-slate-300">🤵 <strong>Entrada novio:</strong> {event.cancion_entrada_novio}</p>
        )}
        {event.cancion_tarta && (
          <p className="text-slate-300">🎂 <strong>Tarta:</strong> {event.cancion_tarta}</p>
        )}
        {event.cancion_padre_hija && (
          <p className="text-slate-300">👨‍👧 <strong>Padre-Hija:</strong> {event.cancion_padre_hija}</p>
        )}
        {event.cancion_madre_hijo && (
          <p className="text-slate-300">👩‍👦 <strong>Madre-Hijo:</strong> {event.cancion_madre_hijo}</p>
        )}
        
        {/* Económico */}
        {event.precio_acordado_whatsapp && (
          <p className="text-emerald-300">💰 <strong>Precio acordado:</strong> {event.precio_acordado_whatsapp}</p>
        )}
        {event.senal_anticipo_whatsapp && (
          <p className="text-emerald-300">💵 <strong>Señal:</strong> {event.senal_anticipo_whatsapp}</p>
        )}
        
        {/* Horarios */}
        {event.horario_inicio_baile_whatsapp && (
          <p className="text-slate-300">⏰ <strong>Inicio baile:</strong> {event.horario_inicio_baile_whatsapp}</p>
        )}
        {event.horario_fin_whatsapp && (
          <p className="text-slate-300">🌙 <strong>Fin:</strong> {event.horario_fin_whatsapp}</p>
        )}
        
        {/* Fotomatón */}
        {event.fotomaton_info && (
          <p className="text-slate-300">📸 <strong>Fotomatón:</strong> {
            typeof event.fotomaton_info === 'object' 
              ? `${event.fotomaton_info.tipo || 'Sí'}${event.fotomaton_info.fondo ? ` - Fondo: ${event.fotomaton_info.fondo}` : ''}${event.fotomaton_info.props ? ` - Props: ${event.fotomaton_info.props}` : ''}`
              : event.fotomaton_info
          }</p>
        )}
        
        {/* Géneros preferidos */}
        {event.generos_preferidos?.length > 0 && (
          <p className="text-green-300">✅ <strong>Géneros preferidos:</strong> {event.generos_preferidos.join(', ')}</p>
        )}
        
        {/* Géneros vetados */}
        {event.generos_vetados?.length > 0 && (
          <p className="text-red-300">🚫 <strong>No poner:</strong> {event.generos_vetados.join(', ')}</p>
        )}
        
        {/* Canciones vetadas */}
        {event.canciones_vetadas?.length > 0 && (
          <p className="text-red-300">🚫 <strong>Canciones vetadas:</strong> {event.canciones_vetadas.join(', ')}</p>
        )}
        
        {/* Momentos especiales */}
        {event.momentos_especiales_whatsapp?.length > 0 && (
          <div className="mt-2">
            <p className="text-amber-400 text-xs font-semibold">✨ Momentos especiales:</p>
            <ul className="text-slate-300 text-xs ml-4 space-y-1">
              {event.momentos_especiales_whatsapp.map((m, idx) => (
                <li key={`momento-${event.id}-${idx}`}>
                  • {typeof m === 'object' ? (
                    <>
                      {m.descripcion}
                      {m.cuando && <span className="text-slate-500"> ({m.cuando})</span>}
                    </>
                  ) : m}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Peticiones especiales */}
        {event.peticiones_whatsapp?.length > 0 && (
          <div className="mt-2">
            <p className="text-slate-400 text-xs font-semibold">📝 Peticiones especiales:</p>
            <ul className="text-slate-300 text-xs ml-4">
              {event.peticiones_whatsapp.map((p, idx) => (
                <li key={`peticion-${event.id}-${idx}`}>• {toDisplayText(p)}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Restricciones */}
        {event.restricciones_whatsapp?.length > 0 && (
          <div className="mt-2">
            <p className="text-orange-400 text-xs font-semibold">⚠️ Restricciones:</p>
            <ul className="text-slate-300 text-xs ml-4">
              {event.restricciones_whatsapp.map((r, idx) => (
                <li key={`restriccion-${event.id}-${idx}`}>• {toDisplayText(r)}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Notas importantes */}
        {event.notas_whatsapp?.length > 0 && (
          <div className="mt-2">
            <p className="text-slate-400 text-xs font-semibold">📌 Notas importantes:</p>
            <ul className="text-slate-300 text-xs ml-4">
              {event.notas_whatsapp.map((n, idx) => (
                <li key={`nota-${event.id}-${idx}`}>• {toDisplayText(n)}</li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Contactos extraídos */}
        {event.contactos_whatsapp?.length > 0 && (
          <div className="mt-2">
            <p className="text-slate-400 text-xs font-semibold">👥 Contactos:</p>
            <ul className="text-slate-300 text-xs ml-4">
              {event.contactos_whatsapp.map((c, idx) => (
                <li key={`contacto-${event.id}-${idx}-${c.nombre}`}>
                  • <span className="text-slate-500">{c.rol}:</span> {c.nombre}
                  {c.telefono && <span className="text-slate-500"> ({c.telefono})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Transcripciones de audios */}
        {event.transcripciones_audios_whatsapp?.length > 0 && (
          <div className="mt-2 pt-2 border-t border-green-700/30">
            <p className="text-amber-400 text-xs font-semibold">🎤 Contenido de audios:</p>
            <ul className="text-slate-300 text-xs ml-4 space-y-1">
              {event.transcripciones_audios_whatsapp.map((t, idx) => (
                <li key={`audio-${event.id}-${idx}`} className="text-slate-400 italic">"{toDisplayText(t)}"</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppInfoCard;
